<template>
  <Button v-slot="slotProps" asChild :aria-label="$t('viewDetailsForStrike', { label: strike.strike_label })">
    <div
      ref="containerRef"
      v-bind="slotProps.a11yAttrs"
      :class="[
        'strike-card-row px-4 h-[72px] flex items-center gap-3 justify-between w-full cursor-pointer hover:bg-(--p-surface-50) dark:hover:bg-(--p-surface-800) transition-colors',
        'border-b border-(--p-content-border-color)',
        props.class || '',
      ]"
      @click="handleStrikeClick"
    >
      <div class="flex-1 min-w-0">
        <p
          class="font-semibold text-[16px] truncate"
          :class="{
            'text-democrat': strike.party === 'Democrat',
            'text-republican': strike.party === 'Republican',
          }"
        >
          {{ strike.strike_label }}
          <i
            v-if="strike.restricted"
            v-tooltip.top="'Restricted'"
            class="ibi-privacy-eye-hide text-sm text-muted-color cursor-help font-normal"
          />
        </p>
        <div class="flex items-center min-w-0" :class="strike.measured_period ? 'gap-1' : 'gap-0'">
          <p class="text-muted-color text-xs truncate">{{ strike.measured_period }}</p>
          <span v-if="strike.measured_period" class="text-muted-color mb-[2px] flex-shrink-0">&bull;</span>
          <AggregateStrikeOpenInterest
            v-if="strike.yesConidExPairs"
            :conid-ex-pairs="strike.yesConidExPairs"
            :seedOpenInterest="strike.open_interest"
            v-slot="{ formattedValue, isLoading }"
          >
            <div class="text-xs flex flex-col flex-shrink-0">
              <Skeleton v-if="isLoading" height=".875rem" width="40px" class="self-end" animation="none" />
              <span class="self-end text-muted-color" v-else>{{ formattedValue }}</span>
            </div>
          </AggregateStrikeOpenInterest>
          <StrikeOpenInterest
            v-else
            :conid_yes="strike.conid_yes"
            :conid_no="strike.conid_no"
            :exchange="exchange"
            :seedOpenInterest="strike.open_interest"
            v-slot="{ formattedValue, isLoading }"
          >
            <div class="text-xs flex flex-col flex-shrink-0">
              <Skeleton v-if="isLoading" height=".875rem" width="40px" class="self-end" animation="none" />
              <span class="self-end text-muted-color" v-else>{{ formattedValue }}</span>
            </div>
          </StrikeOpenInterest>
        </div>
      </div>
      <div class="ml-auto me-1">
        <PositionBadge :conids="allStrikeConids" useTextLayout />
      </div>
      <div class="flex-shrink-0">
        <YesNoBid
          v-if="isSessionVisible"
          :yesConid="strike.conid_yes"
          :noConid="strike.conid_no"
          :yesConidExPairs="strike.yesConidExPairs"
          :noConidExPairs="strike.noConidExPairs"
          :exchange="exchange"
          :multi-exchange="multiExchange"
          :priceSide="priceSide"
          :product-conid="productConid"
          :routing-exchange="routingExchange"
          :isLive="isVisible"
          :seedAskYes="strike.ask_yes"
          :seedAskNo="strike.ask_no"
          :seedBidYes="strike.bid_yes"
          :seedBidNo="strike.bid_no"
          :closed="isStrikeClosed(strike)"
          useMiniCard
          noHover
        />
      </div>
    </div>
  </Button>
</template>

<script setup lang="ts">
import type { ForecastTraderStrike } from '@/types/forecasttrader.types';
import YesNoBid from './YesNoBid.vue';
import useContractActions from '@/composables/useContractActions';
import StrikeOpenInterest from './StrikeOpenInterest.vue';
import AggregateStrikeOpenInterest from './AggregateStrikeOpenInterest.vue';
import PositionBadge from './PositionBadge.vue';
import { useElementVisibility } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useSessionVisibility } from '@/composables/useSessionVisibility';
import { useAppState } from '@/stores/useAppState';
import { conidExPairsToPositionConids } from '@/utils/conidExPairs';
import { isStrikeClosed } from '@/utils/marketClosed';

const appState = useAppState();

const { isSessionVisible } = useSessionVisibility();
const actions = useContractActions();

const containerRef = ref<HTMLDivElement>();
const isVisible = useElementVisibility(containerRef);

interface StrikeCardRowProps {
  strike: ForecastTraderStrike;
  exchange: string;
  multiExchange?: boolean;
  productConid: number;
  /** Exchange paired with productConid for routing. Defaults to exchange. */
  routingExchange?: string;
  class?: string | false;
  origin: string;
  priceSide?: 'bid' | 'ask';
  focusStrikeOnClick?: boolean;
  isLast?: boolean;
}

const props = defineProps<StrikeCardRowProps>();

const allStrikeConids = computed(() =>
  conidExPairsToPositionConids(
    props.strike.conid_yes,
    props.strike.conid_no,
    props.strike.yesConidExPairs,
    props.strike.noConidExPairs,
  ),
);

const handleStrikeClick = () => {
  window.scrollTo(0, 0);
  if (appState.isScrolling) {
    return;
  }
  window.$apm?.logUserInteractionEvent(
    'Card Click',
    'contract-click',
    { conid_yes: `${props.strike.conid_yes}`, origin: props.origin },
    false,
  );
  if (props.focusStrikeOnClick) {
    actions.openQuoteDetails(props.productConid, props.routingExchange ?? props.exchange, props.strike.conid_yes);
  } else {
    actions.openQuoteDetails(props.productConid, props.routingExchange ?? props.exchange);
  }
};
</script>

<style scoped>
.strike-card-row {
  contain: layout style paint;
}
</style>
<!-- last modified by AI tools on 2026-10-13 -->
